import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { AdminHeaderActionsProvider } from './contexts/AdminHeaderActionsContext';
import { InsurerHeaderActionsProvider } from './contexts/InsurerHeaderActionsContext';
import { TabletopHeaderActionsProvider } from './contexts/TabletopHeaderActionsContext';
import { BCPHeaderActionsProvider } from './contexts/BCPHeaderActionsContext';
import { CrisisCommunicationsHeaderActionsProvider } from './contexts/CrisisCommunicationsHeaderActionsContext';
import { PartnerTokenTiersProvider } from './contexts/PartnerTokenTiersContext';
import Navbar from './components/Navbar';
import Breadcrumbs from './components/Breadcrumbs';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPasswordForm from './components/Auth/ForgotPasswordForm';
import ResetPasswordForm from './components/Auth/ResetPasswordForm';
import MagicLink from './pages/MagicLink';
import Dashboard from './pages/Dashboard';
import Academy from './pages/Academy';
import CertificateVerification from './pages/CertificateVerification';
import BCPForm from './pages/BCPForm';
import BCPList from './pages/BCPList';
import SubscriptionManagement from './pages/SubscriptionManagement';
import PaymentHistory from './pages/PaymentHistory';
import BillingSettings from './pages/BillingSettings';
import Profile from './pages/Profile';
import TableTopExercises from './pages/TableTopExercises';
import CrisisCommunications from './pages/CrisisCommunications';
import ImpactAnalysis from './pages/ImpactAnalysis';
import ActionsPage from './pages/ActionsPage';
import DocumentsPage from './pages/DocumentsPage';
import AgentsPage from './pages/AgentsPage';
import AdminPage from './pages/AdminPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import InsurerDashboard from './pages/InsurerDashboard';
import BulkInvite from './pages/BulkInvite';
import RoleGuard from './components/Auth/RoleGuard';
import { isFlashAdmin } from './utils/adminAccess';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import DataProtection from './pages/DataProtection';
import About from './pages/About';
import TabletopScenarioNotification from './components/BCP/TabletopScenarioNotification';
import { useTabletopNotifications } from './hooks/useTabletopNotifications';
import './App.css';

const queryClient = new QueryClient();

// Auth Protected Route Component
const AuthProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-32 h-32 rounded-full border-b-2 border-blue-600 animate-spin"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Root redirect component - redirects admins to admin, insurers to insurer dashboard, others to tabletop exercises
const RootRedirect: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-32 h-32 rounded-full border-b-2 border-blue-600 animate-spin"></div>
      </div>
    );
  }

  if (isFlashAdmin(user)) {
    return <Navigate to="/admin" replace />;
  }

  if (user?.role === 'INSURER') {
    return <Navigate to="/insurer-dashboard" replace />;
  }

  return <Navigate to="/tabletop-exercises" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <PartnerTokenTiersProvider>
            <Router>
              <ScrollToTop />
              <AppContent />
            </Router>
          </PartnerTokenTiersProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

const AppContent: React.FC = () => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const tabletopNotifications = useTabletopNotifications();

  // Don't show navbar/breadcrumbs on auth pages
  const isAuthPage = ['/login', '/register', '/verify-email', '/forgot-password', '/magic-link', '/verify'].includes(location.pathname) ||
                     location.pathname.startsWith('/reset-password/') ||
                     location.pathname.startsWith('/magic-link/') ||
                     location.pathname.startsWith('/verify/');
  const isAdminPage = location.pathname.startsWith('/admin');
  const isWorkspacePage =
    ['/dashboard', '/impact-analysis', '/actions', '/documents', '/agents', '/bcp'].includes(location.pathname) ||
    location.pathname.startsWith('/tabletop-exercises');
  const isInsurerArea = Boolean(
    user &&
      user.role === 'INSURER' &&
      (location.pathname === '/insurer-dashboard' ||
        location.pathname === '/analytics-dashboard' ||
        location.pathname.startsWith('/bulk-invite') ||
        location.pathname === '/profile')
  );
  const MainTag = isAdminPage || isWorkspacePage || isInsurerArea ? 'div' : 'main';

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-32 h-32 rounded-full border-b-2 border-blue-600 animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminHeaderActionsProvider>
    <InsurerHeaderActionsProvider>
    <TabletopHeaderActionsProvider>
    <BCPHeaderActionsProvider>
    <CrisisCommunicationsHeaderActionsProvider>
    <div className="App flex flex-col min-h-screen">
      {!isAuthPage && !isAdminPage && !isWorkspacePage && <Navbar />}
      {!isAuthPage && !isAdminPage && !isWorkspacePage && !isInsurerArea && <Breadcrumbs />}
      <MainTag className={isAdminPage || isWorkspacePage || isInsurerArea ? 'flex-1 flex flex-col' : (!isAuthPage ? 'container px-4 py-8 mx-auto flex-1' : 'flex-1')}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <AuthProtectedRoute>
                      <RootRedirect />
                    </AuthProtectedRoute>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPasswordForm />} />
                <Route path="/reset-password/:token" element={<ResetPasswordForm />} />
                <Route path="/magic-link/:token" element={<MagicLink />} />
                <Route
                  path="/dashboard"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={[]} requireFlashAdmin redirectTo="/tabletop-exercises">
                        <Dashboard />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/impact-analysis"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={[]} requireFlashAdmin redirectTo="/tabletop-exercises">
                        <ImpactAnalysis />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/actions"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={[]} requireFlashAdmin redirectTo="/tabletop-exercises">
                        <ActionsPage />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={[]} requireFlashAdmin redirectTo="/tabletop-exercises">
                        <DocumentsPage />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/agents"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={[]} requireFlashAdmin redirectTo="/tabletop-exercises">
                        <AgentsPage />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/bcp"
                  element={
                    <AuthProtectedRoute>
                      <BCPList />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/bcp/new"
                  element={
                    <AuthProtectedRoute>
                      <BCPForm />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/bcp/:id"
                  element={
                    <AuthProtectedRoute>
                      <BCPForm />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/subscription-management"
                  element={
                    <AuthProtectedRoute>
                      <SubscriptionManagement />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/payment-history"
                  element={
                    <AuthProtectedRoute>
                      <PaymentHistory />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/billing-settings"
                  element={
                    <AuthProtectedRoute>
                      <BillingSettings />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <AuthProtectedRoute>
                      <Profile />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/tabletop-exercises/*"
                  element={
                    <AuthProtectedRoute>
                      <TableTopExercises />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/crisis-communications"
                  element={
                    <AuthProtectedRoute>
                      <CrisisCommunications />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/academy"
                  element={
                    <AuthProtectedRoute>
                      <Academy />
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/admin/templates"
                  element={<Navigate to="/admin" replace />}
                />
                <Route
                  path="/admin"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={[]} requireFlashAdmin redirectTo="/">
                        <AdminPage />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/insurer-dashboard"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={['INSURER']} redirectTo="/">
                        <InsurerDashboard />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/analytics-dashboard"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={['INSURER']} requireFlashAdmin redirectTo="/">
                        <AnalyticsDashboard />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route
                  path="/bulk-invite"
                  element={
                    <AuthProtectedRoute>
                      <RoleGuard allowedRoles={['INSURER']} redirectTo="/insurer-dashboard">
                        <BulkInvite />
                      </RoleGuard>
                    </AuthProtectedRoute>
                  }
                />
                <Route path="/verify" element={<CertificateVerification />} />
                <Route path="/verify/:code" element={<CertificateVerification />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/data-protection" element={<DataProtection />} />
                <Route path="/about" element={<About />} />
              </Routes>
      </MainTag>
      {!isAuthPage && !isAdminPage && !isWorkspacePage && <Footer />}

      {/* Tabletop Scenario Notifications */}
      {isAuthenticated && (
        <TabletopScenarioNotification
          isOpen={tabletopNotifications.showNotification}
          onClose={tabletopNotifications.closeNotification}
          scenarios={tabletopNotifications.scenarios}
          bcpName={tabletopNotifications.bcpName}
        />
      )}
    </div>
    </CrisisCommunicationsHeaderActionsProvider>
    </BCPHeaderActionsProvider>
    </TabletopHeaderActionsProvider>
    </InsurerHeaderActionsProvider>
    </AdminHeaderActionsProvider>
  );
};

export default App;